import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { NotificationService } from '@shared/services/notification.service';
import { PageHeaderComponent } from '@shared/components/layout';
import { MinistryManagementService, MinistryDto, CreateMinistryDto, UpdateMinistryDto } from '../../services/ministry-management.service';
import { getApiErrorMessage } from '@core/http/api-error-message.util';

/**
 * Componente para crear y editar ministerios
 */
@Component({
  selector: 'app-ministry-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PageHeaderComponent
  ],
  templateUrl: './ministry-editor.component.html',
  styleUrl: './ministry-editor.component.scss'
})
export class MinistryEditorComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  ministryId: number | null = null;
  currentMinistry: MinistryDto | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private service: MinistryManagementService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['id'] && params['id'] !== 'new') {
          this.ministryId = +params['id'];
          this.isEditMode = true;
          this.loadMinistry();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializa el formulario
   */
  private initializeForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]]
    });
  }

  /**
   * Carga el ministerio para editar
   */
  private loadMinistry(): void {
    if (!this.ministryId) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getById(this.ministryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ministry: MinistryDto) => {
          this.currentMinistry = ministry;
          this.form.patchValue({
            name: ministry.name
          });
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading ministry:', error);
          this.notificationService.error(getApiErrorMessage(error, 'No se pudo cargar el ministerio seleccionado'));
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Guarda el ministerio (crear o actualizar)
   */
  saveMinistry(): void {
    if (!this.form.valid) {
      this.notificationService.warning('Por favor, completa todos los campos correctamente');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const formValue = this.form.value;

    if (this.isEditMode && this.ministryId && this.currentMinistry) {
      // En modo edición, preservar isActive y sortOrder del ministerio actual
      const updateDto: UpdateMinistryDto = {
        ...formValue,
        isActive: this.currentMinistry.isActive,
        sortOrder: this.currentMinistry.sortOrder
      };
      this.updateMinistry(this.ministryId, updateDto);
    } else {
      this.createMinistry(formValue);
    }
  }

  /**
   * Crea un nuevo ministerio
   */
  private createMinistry(dto: CreateMinistryDto): void {
    this.service.create(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Ministerio creado exitosamente');
          this.router.navigate(['/configuration/ministries']);
        },
        error: (error: any) => {
          console.error('Error creating ministry:', error);
          this.notificationService.error(getApiErrorMessage(
            error,
            'No se pudo crear el ministerio. Revisá los datos cargados e intentá nuevamente'
          ));
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Actualiza un ministerio existente
   */
  private updateMinistry(id: number, dto: UpdateMinistryDto): void {
    this.service.update(id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Ministerio actualizado exitosamente');
          this.router.navigate(['/configuration/ministries']);
        },
        error: (error: any) => {
          console.error('Error updating ministry:', error);
          this.notificationService.error(getApiErrorMessage(
            error,
            'No se pudo actualizar el ministerio. Revisá los datos cargados e intentá nuevamente'
          ));
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Cancela la edición y regresa al listado
   */
  cancel(): void {
    this.router.navigate(['/configuration/ministries']);
  }
}
